import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, Subject, from , of} from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import JSZip from 'jszip';
import { MessageService } from 'primeng/api';
import { VlHttpService, APIConfigMapping } from 'usermgmt';
import { DEFAULT_USER_ID } from '../../constants/proj.cnst';
import { findPolledVersion } from './document-version.util';
import { StructureTypeItem } from '../../features/digital-library/models/structure.model';
import { applyApiAttributeTypes } from '../../features/digital-library/components/assets-validation/constants/rail-form-options.constant';

export interface StationSurvey {
  lidar: boolean;
  ortho: boolean;
  ts: boolean;
}

export interface Station {
  id: number;
  name: string;
  code: string;
  section: string;
  zone: string;
  division: string;
  esp: 'approved' | 'digitised' | 'validated' | 'under_review' | 'pending_review' | null;
  sip: 'approved' | 'digitised' | 'validated' | 'under_review' | 'uploaded' | null;
  lop: 'approved' | 'digitised' | null;
  toc: boolean;
  survey: StationSurvey;
  versions: string | null;
  lastTime: string;
  lastBy: string;
  stationClass?: string;
  cll?: string;
  stationId?: string;
  stationTitle?: string;
  trainDirection?: string;
  adjacentUp?: string;
  adjacentDn?: string;
}

export interface DocumentSubmissionPayload {
  station_id: string;
  user_id: string;
  file_name: string;
  document_type_code?: string;
  drawing_number?: string;
  ipu_number?: string;
  source_esp_version_id?: string;
  additional_info?: {
    turnout_id_pattern?: string[];
    [key: string]: any;
  };
  allow_duplicate?: boolean;
  [key: string]: unknown;
}

export interface ExtractionPayload {
  zone_id: string;
  division_id: string;
  section_id: string;
  station_id: string;
  document_type: string;
  station_name: string;
  file_name: string;
}

export function getLoggedInUserName(): string {
  try {
    const infoStr = typeof localStorage !== 'undefined' ? localStorage.getItem('userInfo') : null;
    if (infoStr) {
      const info = JSON.parse(infoStr);
      const first = info.firstName || info.first_name || '';
      const last = info.lastName || info.last_name || '';
      const fullName = `${first} ${last}`.trim();
      if (fullName) return fullName;
    }
  } catch {
    console.log("Failed to fetch user details from localstorage");
  }
  return '';
}

@Injectable({ providedIn: 'root' })
export class StationService {
  private readonly vlHttp = inject(VlHttpService);
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);

  getLoggedInUserName(): string {
    return getLoggedInUserName();
  }

  stationsSignal = signal<Station[]>([]);
  zonesSignal = signal<any[]>([]);
  divisionsSignal = signal<any[]>([]);
  sectionsSignal = signal<any[]>([]);
  structureTypesSignal = signal<StructureTypeItem[]>([]);
  activeAssetsData = signal<any>(null);
  activeDxfFile = signal<File | null>(null);
  activeUploadedDxfFile = signal<File | null>(null);
  activeGeneratedDxfFile = signal<File | null>(null);

  activePolls = new Map<string, ReturnType<typeof setInterval>>();
  pollingCompleted = new Subject<{ submissionId: string; status: 'SUCCESS' | 'FAILED' }>();

  stationDocuments = signal<Record<string, Record<string, any[]>>>({
    BVRT: {
      esp: [
        {
          fileName: 'architectural_-_annotation_scaling_and_multileaders.dwg',
          version: '7.0',
          status: 'Approved',
          tone: 'success',
          user: 'Laxmi Prasad',
          initials: 'VP',
          role: 'Minister of Railways',
          uploaded: '1 day ago',
          approvalStatus: 'Approved'
        }
      ],
      sip: [],
      lop: [],
      survey: [
        {
          fileName: 'BVRT_LiDAR_Survey.las',
          version: '1.0',
          status: 'Completed',
          tone: 'success',
          user: 'Laxmi Prasad',
          initials: 'VP',
          role: 'SSE',
          uploaded: '2 days ago',
          approvalStatus: 'Approved'
        }
      ]
    },
    BZA: {
      esp: [
        {
          fileName: 'BZA_ESP_V1.dwg',
          version: '1.0',
          status: 'Approved',
          tone: 'success',
          user: 'Laxmi Prasad',
          initials: 'VP',
          role: 'SSE',
          uploaded: '1 day ago',
          approvalStatus: 'Approved'
        }
      ],
      sip: [],
      lop: [],
      survey: []
    }
  });

  private getRelativeTimeString(dateStr: string): string {
    if (!dateStr) return 'Just added';
    try {
      const cleanStr = dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + '+05:30';
      const past = new Date(cleanStr);
      const diffMs = Date.now() - past.getTime();
      if (isNaN(diffMs)) return 'Just added';

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 7) return `${diffDays} days ago`;

      return past.toLocaleDateString();
    } catch {
      return 'Just added';
    }
  }

  private isZipUrl(url: string): boolean {
    if (!url) return false;
    try {
      const pathname = new URL(url, 'http://dummy.local').pathname;
      return pathname.toLowerCase().endsWith('.zip');
    } catch {
      const cleanUrl = url.split('?')[0].split('#')[0];
      return cleanUrl.toLowerCase().endsWith('.zip');
    }
  }

  getStationsRaw(): Observable<Station[]> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_STATIONS_RAW_V1']
    });
  }

  getStations(): Observable<Station[]> {
    return forkJoin({
      zones: this.getZones(),
      divisions: this.getDivisions(),
      sections: this.getSections(),
      stations: this.getStationsRaw()
    }).pipe(
      map((res: any) => {
        const rawStations = Array.isArray(res.stations) ? res.stations : (res.stations?.data || res.stations?.stations || []);
        const zones = res.zones || [];
        const divisions = res.divisions || [];
        const sections = res.sections || [];

        this.zonesSignal.set(zones);
        this.divisionsSignal.set(divisions);
        this.sectionsSignal.set(sections);

        const mapped = rawStations.map((station: any) => {
          const sectionObj = sections.find((sec: any) => sec.id === station.section_id);
          const divisionObj = sectionObj ? divisions.find((div: any) => div.id === sectionObj.division_id) : null;
          const zoneObj = divisionObj ? zones.find((z: any) => z.id === divisionObj.zone_id) : null;

          const docs = station.documents || [];
          const espDoc = docs.find((d: any) => d.document_type === 'ESP');
          const sipDoc = docs.find((d: any) => d.document_type === 'SIP');
          const lopDoc = docs.find((d: any) => d.document_type === 'LOP');

          const uploadedByType = station.uploaded_files_by_type || {};
          const versionsByType = station.document_versions_by_type || {};

          const getDocStatus = (type: string, legacyDoc: any, legacyProp: any) => {
            const count = uploadedByType[type] ?? versionsByType[type];
            if (count !== undefined && count !== null) {
              return count > 0 ? 'approved' : null;
            }
            if (legacyDoc) {
              return legacyDoc.count > 0 ? 'approved' : null;
            }
            return legacyProp || null;
          };

          return {
            ...station,
            zone: zoneObj?.code ?? 'SCR',
            division: divisionObj?.name ?? 'Vijayawada',
            section: sectionObj?.name ?? 'Nidadavolu-Bhimavaram',
            esp: getDocStatus('ESP', espDoc, station.esp),
            sip: getDocStatus('SIP', sipDoc, station.sip),
            lop: getDocStatus('LOP', lopDoc, station.lop),
            toc: station.toc || false,
            survey: station.survey || { lidar: false, ortho: false, ts: false },
            versions: station.versions || null,
            lastActivityDate: station.last_updated_at || station.created_at || new Date().toISOString(),
            lastTime: this.getRelativeTimeString(station.last_updated_at || station.created_at),
            lastBy: (() => {
              const isUuid = (val: string) => typeof val === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
              const by = station.last_updated_by || station.created_by;
              if (by && !isUuid(by)) {
                return by.charAt(0).toUpperCase() + by.slice(1);
              }
              return getLoggedInUserName();
            })()
          };
        });
        this.stationsSignal.set(mapped);
        return mapped;
      })
    );
  }

  getClonedRows(code: string): Record<string, any[]> {
    return this.stationDocuments()[code.toUpperCase()] || { esp: [], sip: [], lop: [], survey: [] };
  }

  updateClonedRows(code: string, newRows: Record<string, any[]>): void {
    this.stationDocuments.update(prev => ({ ...prev, [code.toUpperCase()]: newRows }));
  }

  getStationByCode(code: string): Station | undefined {
    return this.stationsSignal().find(s => s.code.toLowerCase() === code.toLowerCase());
  }

  getZones(): Observable<any[]> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_ZONES_V1']
    });
  }

  getDivisions(): Observable<any[]> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_DIVISIONS_V1']
    });
  }

  getSections(): Observable<any[]> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_SECTIONS_V1']
    });
  }

  getStructureTypes(): Observable<StructureTypeItem[]> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_STRUCTURE_TYPES']
    }).pipe(
      map((res: any) => {
        const rawList: any[] = Array.isArray(res) ? res : (res?.data || res?.items || []);

        const normalized: StructureTypeItem[] = rawList
          .filter((item: any) => item && item.is_active !== false)
          .map((item: any) => ({
            id: String(item.id || '').trim(),
            code: String(item.code || '').trim(),
            description: item.description ? String(item.description).trim() : undefined,
            is_active: item.is_active ?? true,
            created_at: item.created_at,
            ...item
          }))
          .filter(item => Boolean(item.id && item.code));

        if (normalized.length > 0) {
          this.structureTypesSignal.set(normalized);
        }
        return normalized;
      }),
      catchError(err => {
        console.warn('Failed to fetch structure types from API:', err);
        return of(this.structureTypesSignal());
      })
    );
  }

  getAssetAttributeTypes(): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_ASSET_ATTRIBUTE_TYPES']
    }).pipe(
      map((res: any) => {
        if (res) {
          applyApiAttributeTypes(res);
        }
        return res;
      }),
      catchError(err => {
        console.warn('Failed to fetch asset attribute types from API:', err);
        return of(null);
      })
    );
  }

  findStructureType(identifier: string): StructureTypeItem | undefined {
    if (!identifier) return undefined;
    const clean = String(identifier).trim();
    const cleanUpper = clean.toUpperCase();
    const currentTypes = this.structureTypesSignal();
    if (!currentTypes || currentTypes.length === 0) return undefined;

    // 1. Direct ID match (exact or case-insensitive)
    const byId = currentTypes.find(t => t.id === clean || t.id.toLowerCase() === clean.toLowerCase());
    if (byId) return byId;

    // 2. Direct exact code match
    const byCode = currentTypes.find(t => (t.code || '').toUpperCase() === cleanUpper);
    if (byCode) return byCode;

    // 3. Exact description match
    const byDesc = currentTypes.find(t => (t.description || '').toUpperCase() === cleanUpper);
    if (byDesc) return byDesc;

    // 4. Dynamic keyword / containment match across code and description
    const byContainment = currentTypes.find(t => {
      const codeUpper = (t.code || '').toUpperCase();
      const descUpper = (t.description || '').toUpperCase();
      return (codeUpper && (cleanUpper.includes(codeUpper) || codeUpper.includes(cleanUpper))) ||
             (descUpper && (cleanUpper.includes(descUpper) || descUpper.includes(cleanUpper)));
    });
    if (byContainment) return byContainment;

    return undefined;
  }

  getStationOptions(): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_STATION_OPTIONS']
    });
  }

  getDocumentTypeOptions(): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_DOCUMENT_TYPE_OPTIONS']
    });
  }

  addStation(station: any): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['ADD_STATION_V1'],
      postData: station
    });
  }

  updateStation(idOrCode: any, updates: Partial<Station>): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['UPDATE_STATION_V1'],
      postData: { id: idOrCode, ...updates }
    });
  }

  deleteStation(idOrCode: any): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['DELETE_STATION_V1'],
      postData: { id: idOrCode }
    });
  }

  documentSubmission(payload: DocumentSubmissionPayload): Observable<any> {
    const { document_type_code, ...rest } = payload as any;
    const docType = (document_type_code || 'esp').toLowerCase();
    const cleanPayload: Record<string, any> = {
      docType,
      station_id: rest.station_id || '',
      user_id: rest.user_id || DEFAULT_USER_ID,
      file_name: rest.file_name || ''
    };

    if (rest.drawing_number || rest.drawingNumber) {
      cleanPayload['drawing_number'] = rest.drawing_number || rest.drawingNumber;
    }
    if (rest.ipu_number || rest.ipuNumber) {
      cleanPayload['ipu_number'] = rest.ipu_number || rest.ipuNumber;
    }
    const additionalInfo = rest.additional_info || rest.additionalInfo || (rest.turnout_id_pattern || rest.turnoutIdPattern ? { turnout_id_pattern: rest.turnout_id_pattern || rest.turnoutIdPattern } : null);
    if (additionalInfo) {
      cleanPayload['additional_info'] = additionalInfo;
    }
    if (rest.horizontal_scale || rest.horizontalScale) {
      cleanPayload['horizontal_scale'] = rest.horizontal_scale || rest.horizontalScale;
    }
    if (rest.height_of_layout || rest.heightOfLayout) {
      cleanPayload['height_of_layout'] = rest.height_of_layout || rest.heightOfLayout;
    }
    if (rest.name_of_the_proposed_work || rest.nameOfTheProposedWork) {
      cleanPayload['name_of_the_proposed_work'] = rest.name_of_the_proposed_work || rest.nameOfTheProposedWork;
    }
    if (rest.date_of_drawing_number_issued || rest.dateOfDrawingNumberIssued) {
      cleanPayload['date_of_drawing_number_issued'] = rest.date_of_drawing_number_issued || rest.dateOfDrawingNumberIssued;
    }
    if (rest.previous_drawing_number || rest.previousDrawingNumber) {
      cleanPayload['previous_drawing_number'] = rest.previous_drawing_number || rest.previousDrawingNumber;
    }
    if (rest.track_circuiting_type_info || rest.trackCircuitingTypeInfo) {
      cleanPayload['track_circuiting_type_info'] = rest.track_circuiting_type_info || rest.trackCircuitingTypeInfo;
    }
    if (rest.section_type_details || rest.sectionTypeDetails) {
      cleanPayload['section_type_details'] = rest.section_type_details || rest.sectionTypeDetails;
    }
    if (rest.source_esp_version_id || rest.parent_submission_id) {
      cleanPayload['source_esp_version_id'] = rest.source_esp_version_id || rest.parent_submission_id;
    }
    if (rest['color-to-action-mapping'] || rest.color_to_action_mapping || rest.colorToActionMapping) {
      cleanPayload['color-to-action-mapping'] = rest['color-to-action-mapping'] || rest.color_to_action_mapping || rest.colorToActionMapping;
    }
    if (rest.allow_duplicate !== undefined) {
      cleanPayload['allow_duplicate'] = !!rest.allow_duplicate;
    }

    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['DOCUMENT_SUBMISSION_INITIATE'],
      postData: cleanPayload
    });
  }

  checkReferenceAvailability(docType: string, referenceNumber: string, stationId: string): Observable<any> {
    const cleanDocType = (docType || 'esp').toUpperCase();
    const params: Record<string, string> = {
      document_type_code: cleanDocType,
      reference_number: referenceNumber,
      station_id: stationId
    };
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['CHECK_REFERENCE_AVAILABILITY'],
      options: { params }
    });
  }

  getDocumentReferences(
    docType: string = 'ESP',
    stationId?: string,
    query?: string,
    limit: number = 50,
    offset: number = 0
  ): Observable<any> {
    const cleanDocType = (docType || 'ESP').toUpperCase();
    const params: Record<string, string> = {
      document_type_code: cleanDocType,
      limit: limit.toString(),
      offset: offset.toString()
    };
    if (stationId) {
      params['station_id'] = stationId;
    }
    if (query && query.trim()) {
      params['q'] = query.trim();
    }
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_DOCUMENT_REFERENCES'],
      options: { params }
    });
  }

  getSubmissions(stationId: string, userId: string = DEFAULT_USER_ID): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_SUBMISSIONS'],
      options: {
        params: { station_id: stationId, user_id: userId }
      }
    });
  }

  getSubmissionStatus(submissionId: string): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_SUBMISSION_STATUS'],
      postData: { submissionId }
    });
  }

  getVersionAssetsSummary(versionId: string, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_VERSION_ASSETS_SUMMARY'],
      postData: { docType: cleanDocType, versionId }
    });
  }

  getVersionAssetTypeDetails(versionId: string, assetType: string, page: number = 1, pageSize: number = 100, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase(); 
    const cleanAssetType = (assetType || 'track').toLowerCase().trim().replace(/[\s-]+/g, '_');
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_VERSION_ASSET_TYPE_DETAILS'],
      postData: { docType: cleanDocType, versionId, assetType: cleanAssetType },
      options: {
        params: { page: page.toString(), page_size: pageSize.toString() }
      }
    });
  }

  patchVersionAssetTypeBatch(versionId: string, assetType: string, items: any[], cascade: boolean = false, updatedBy: string = DEFAULT_USER_ID, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    const cleanAssetType = (assetType || 'track').toLowerCase().trim().replace(/[\s-]+/g, '_');
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['PATCH_VERSION_ASSET_TYPE_BATCH'],
      postData: {
        docType: cleanDocType,
        versionId,
        assetType: cleanAssetType,
        updated_by: updatedBy,
        cascade: cascade,
        items: items
      }
    });
  }

  uploadDxfToPresignedUrl(uploadUrl: string, fileOrBlob: Blob | File): Observable<any> {
    return from(
      fetch(uploadUrl, {
        method: 'PUT',
        body: fileOrBlob,
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      }).then(res => {
        if (!res.ok) {
          throw new Error(`DXF upload failed with status ${res.status}`);
        }
        return res;
      })
    );
  }

  confirmAssetDxfUpload(versionId: string, uploadToken: string, assetType: string, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    const cleanAssetType = (assetType || 'track').toLowerCase().trim().replace(/[\s-]+/g, '_');
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['CONFIRM_ASSET_DXF_UPLOAD'],
      postData: {
        docType: cleanDocType,
        versionId,
        upload_token: uploadToken,
        asset_type: cleanAssetType
      }
    });
  }

  getVersionAssets(versionId: string, docType: string = 'esp'): Observable<any> {
    return this.getVersionAssetsSummary(versionId, docType);
  }

  getSubmissionAssets(submissionId: string, userId: string = DEFAULT_USER_ID, expiresIn: number = 3600, docType: string = 'esp'): Observable<any> {
    return this.getVersionAssetsSummary(submissionId, docType);
  }

  patchVersionAssetItem(versionId: string, assetType: string, assetId: string, payload: any, docType: string = 'sip'): Observable<any> {
    const cleanDocType = (docType || 'sip').toLowerCase();
    const cleanAssetType = (assetType || 'track').toLowerCase().trim().replace(/[\s-]+/g, '_');
    return this.http.patch(`/api/v1/${cleanDocType}/${versionId}/assets/${cleanAssetType}/${assetId}`, payload);
  }

  deleteVersionAssetItem(versionId: string, assetType: string, assetId: string, updatedBy: string = DEFAULT_USER_ID, cascade: boolean = false, docType: string = 'sip'): Observable<any> {
    const cleanDocType = (docType || 'sip').toLowerCase();
    const cleanAssetType = (assetType || 'track').toLowerCase().trim().replace(/[\s-]+/g, '_');
    return this.http.delete(`/api/v1/${cleanDocType}/${versionId}/assets/${cleanAssetType}/${assetId}`, {
      params: { updated_by: updatedBy, cascade: String(cascade) }
    });
  }

  getVersionAssetDependents(versionId: string, assetType: string, assetId: string, docType: string = 'sip'): Observable<any> {
    const cleanDocType = (docType || 'sip').toLowerCase();
    const cleanAssetType = (assetType || 'track').toLowerCase().trim().replace(/[\s-]+/g, '_');
    return this.http.get(`/api/v1/${cleanDocType}/${versionId}/assets/${cleanAssetType}/${assetId}/dependents`);
  }

  getTableTypes(versionId: string, docType: string = 'sip'): Observable<{ options: any[] }> {
    const cleanDocType = (docType || 'sip').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_TABLE_TYPES'],
      postData: {
        docType: cleanDocType,
        versionId
      }
    });
  }

  moveTable(versionId: string, payload: any, docType: string = 'sip'): Observable<any> {
    const cleanDocType = (docType || 'sip').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['MOVE_TABLE'],
      postData: {
        docType: cleanDocType,
        versionId,
        ...payload
      }
    });
  }

  getSubmissionVersions(submissionId: string, userId: string = DEFAULT_USER_ID): Observable<any[]> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_SUBMISSION_VERSIONS'],
      postData: { submissionId },
      options: {
        params: { user_id: userId }
      }
    });
  }

  initiateVersionUpload(submissionId: string, userId: string = DEFAULT_USER_ID, expiresIn: number = 3600): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['INITIATE_VERSION_UPLOAD'],
      postData: { submissionId },
      options: {
        params: { user_id: userId, expires_in: expiresIn.toString() }
      }
    });
  }

  initiateDigitalise(versionId: string, docType: string = 'esp', userId: string = DEFAULT_USER_ID): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['INITIATE_DIGITALISE'],
      postData: { docType: cleanDocType, versionId },
      options: {
        params: { user_id: userId }
      }
    });
  }

  confirmDigitalise(versionId: string, docType: string = 'esp', userId: string = DEFAULT_USER_ID): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['CONFIRM_DIGITALISE'],
      postData: { docType: cleanDocType, versionId },
      options: {
        params: { user_id: userId }
      }
    });
  }

  getStationDocumentVersions(stationId: string, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['GET_STATION_DOCUMENT_VERSIONS'],
      postData: { docType: cleanDocType, stationId }
    });
  }

  confirmUpload(versionId: string, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['CONFIRM_DOCUMENT_UPLOAD'],
      postData: { docType: cleanDocType, versionId }
    });
  }

  confirmSubmission(submissionId: string, docType: string = 'esp'): Observable<any> {
    return this.confirmUpload(submissionId, docType);
  }

  uploadAndConfirm(presignedUrl: string, file: File, versionId: string, docType: string = 'esp'): Observable<any> {
    return this.uploadToPresignedUrl(presignedUrl, file).pipe(
      switchMap(() => this.confirmUpload(versionId, docType)),
      catchError(err => {
        if (err.status === 409) {
          console.warn(`[StationService] Confirm returned 409 for ${versionId}. Retrying PUT and confirm...`);
          return this.uploadToPresignedUrl(presignedUrl, file).pipe(
            switchMap(() => this.confirmUpload(versionId, docType))
          );
        }
        throw err;
      })
    );
  }

  confirmVersionUpload(submissionId: string, versionId: string, userId: string = DEFAULT_USER_ID): Observable<any> {
    return this.confirmUpload(versionId, 'esp');
  }

  deleteVersion(versionId: string, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['DELETE_DOCUMENT_VERSION'],
      postData: { docType: cleanDocType, versionId }
    });
  }

  deleteSubmission(submissionId: string, stationId?: string, docType: string = 'esp'): Observable<any> {
    return this.deleteVersion(submissionId, docType);
  }

  documentStatusUpdate(submissionId: string, userId: string = DEFAULT_USER_ID): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['DOCUMENT_STATUS_UPDATE'],
      postData: { submissionId },
      options: {
        params: { user_id: userId }
      }
    });
  }

  triggerExtraction(submissionId: string, userId: string = DEFAULT_USER_ID, payload: ExtractionPayload = {} as ExtractionPayload): Observable<any> {
    return this.vlHttp.placeAPICall({
      ...APIConfigMapping['TRIGGER_EXTRACTION'],
      postData: { submissionId, ...payload },
      options: {
        params: { user_id: userId }
      }
    });
  }

  uploadToPresignedUrl(url: string, file: File | Blob): Observable<any> {
    const contentType = (file instanceof File ? file.type : '') || 'application/octet-stream';
    return this.http.put(url, file, {
      headers: { 'Content-Type': contentType }
    });
  }

  convertDecimalStringsToFloats(data: any, visited = new Set<any>()): any {
    if (data === null || data === undefined) return data;
    if (typeof data !== 'object') {
      if (typeof data === 'string') {
        const trimmed = data.trim();
        if (trimmed !== '' && !isNaN(Number(trimmed)) && isFinite(Number(trimmed))) {
          if (trimmed.includes('.')) {
            return parseFloat(trimmed);
          }
        }
      }
      return data;
    }

    if (visited.has(data)) return data;
    visited.add(data);

    if (Array.isArray(data)) {
      return data.map(item => this.convertDecimalStringsToFloats(item, visited));
    }

    const result: any = {};
    for (const key of Object.keys(data)) {
      const val = data[key];
      const lowerKey = key.toLowerCase();
      const isNumericField = (
        lowerKey.includes('location_km') ||
        lowerKey.includes('location_chainage') ||
        lowerKey.includes('start_location_km') ||
        lowerKey.includes('start_location_chainage') ||
        lowerKey.includes('end_location_km') ||
        lowerKey.includes('end_location_chainage') ||
        lowerKey.includes('fcsb') ||
        lowerKey.includes('fc_value') ||
        lowerKey === 'chainage' ||
        lowerKey === 'distance_km' ||
        lowerKey === 'distance' ||
        lowerKey === 'distance_details_distance' ||
        lowerKey === 'junction_location_km' ||
        lowerKey === 'junction_location_chainage' ||
        lowerKey === 'centerline_location_km' ||
        lowerKey === 'center_line_location_chainage' ||
        lowerKey === 'km' ||
        lowerKey === 'rl' ||
        lowerKey === 'rl_value' ||
        lowerKey === 'turnout_angle' ||
        lowerKey.includes('gradient_value') ||
        lowerKey === 'dimensions_length_m' ||
        lowerKey === 'length_m' ||
        lowerKey === 'dimension'
      );

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed !== '' && !isNaN(Number(trimmed)) && isFinite(Number(trimmed))) {
          if (trimmed.includes('.') || isNumericField) {
            result[key] = parseFloat(trimmed);
            continue;
          }
        }
      }
      result[key] = this.convertDecimalStringsToFloats(val, visited);
    }
    return result;
  }

  updateAssetsJson(url: string, json: any, fileName: string = 'assets.json'): Observable<any> {
    const rawObj = typeof json === 'string' ? JSON.parse(json) : json;
    const processedJson = this.convertDecimalStringsToFloats(rawObj);
    const jsonString = JSON.stringify(processedJson, null, 2);

    const zip = new JSZip();
    zip.file(fileName, jsonString);
    const zipBlobPromise = zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });

    return from(zipBlobPromise).pipe(
      switchMap((zipBlob: Blob) =>
        this.http.put(url, zipBlob, { headers: { 'Content-Type': 'application/zip' } })
      )
    );
  }

  savePimAssetsJson(versionId: string, json: any, docType: string = 'esp'): Observable<any> {
    const cleanDocType = (docType || 'esp').toLowerCase();
    const cleanJson = this.convertDecimalStringsToFloats(json);
    console.log('[StationService] savePimAssetsJson payload:', cleanJson);
    const extracted = cleanJson?.extracted_elements || {};

    const keyToBackendMap: Record<string, string> = {
      tracks: 'track',
      track: 'track',
      turnouts: 'turnout',
      turnout: 'turnout',
      stock_rail_joints: 'stock_rail_joint',
      stock_rail_joint: 'stock_rail_joint',
      trap_points: 'trap_point',
      trap_point: 'trap_point',
      dead_ends: 'dead_end',
      dead_end: 'dead_end',
      turning_points: 'turning_point',
      turning_point: 'turning_point',
      gates: 'gate',
      gate: 'gate',
      lc_gates: 'gate',
      lc_gate: 'gate',
      structures: 'structure',
      structure: 'structure',
      fouling_marks: 'fouling_mark',
      fouling_mark: 'fouling_mark',
      gradients: 'gradient',
      yard_boundaries: 'yard_boundary',
      yard_boundary: 'yard_boundary',
      railway_boundary_lines: 'yard_boundary',
      // track_circuit_sections: 'track_circuit_section_name',
      // track_circuit_section: 'track_circuit_section_name',
      track_circuit_section_name: 'track_circuit_section_name',
      center_to_center_distances: 'track_to_track_distance',
      center_to_center_track_distances: 'track_to_track_distance',
      track_to_track_distances: 'track_to_track_distance',
      track_to_track_distance: 'track_to_track_distance',
      track_to_structure_distances: 'track_to_structure_distance',
      track_to_structure_distance: 'track_to_structure_distance',
      structure_to_structure_distances: 'structure_to_structure_distance',
      structure_to_structure_distance: 'structure_to_structure_distance',
      centerlines: 'central_line_location',
      central_line_location: 'central_line_location',
      center_line_location: 'central_line_location',
      platforms: 'platform',
      platform: 'platform',
      bridges: 'bridge',
      bridge: 'bridge',
      adjacent_stations: 'adjacent_station',
      adjacent_station: 'adjacent_station'
    };

    const requests: Observable<any>[] = [];

    for (const [key, items] of Object.entries(extracted)) {
      const itemList = Array.isArray(items)
        ? items
        : (items && typeof items === 'object' ? Object.values(items) : []);

      if (itemList.length > 0) {
        const backendType = keyToBackendMap[key] || key;
        const sanitizedItems = itemList.map(item => this.sanitizeAssetItem(item));
        requests.push(
          this.patchVersionAssetTypeBatch(versionId, backendType, sanitizedItems, false, DEFAULT_USER_ID, cleanDocType).pipe(
            catchError((err: unknown) => {
              console.warn(`[StationService] Batch patch warning for ${backendType}:`, err);
              return of(null);
            })
          )
        );
      }
    }

    if (requests.length === 0) {
      return of(cleanJson);
    }

    return forkJoin(requests).pipe(map(() => cleanJson));
  }

  private sanitizeAssetItem(item: any): any {
    if (!item || typeof item !== 'object') return item;
    const clean = { ...item };
    delete clean.drawing;
    delete clean.highlight;
    delete clean.highlight_entities;
    delete clean.computed_data;
    delete clean.ui_id;
    delete clean._uiState;
    delete clean._isEditing;
    delete clean._isNew;
    return clean;
  }

  fetchJsonFromUrl(url: string): Observable<any> {
    const parseJsonText = (text: string) => {
      const parsed = JSON.parse(text);
      const converted = this.convertDecimalStringsToFloats(parsed);
      return converted;
    };

    const unzipJsonBlob = (blob: Blob): Observable<string> => {
      return from(JSZip.loadAsync(blob)).pipe(
        switchMap((zip: JSZip) => {
          const entries = Object.keys(zip.files).filter(k => !zip.files[k].dir);
          if (entries.length === 0) throw new Error('Zip archive contained no files');
          const jsonEntry = entries.find(e => e.toLowerCase().endsWith('.json')) || entries[0];
          return from(zip.files[jsonEntry].async('text'));
        }),
        catchError((zipErr: any) => {
          console.warn('[StationService] Unzipping JSON failed, reading as plain text:', zipErr);
          return from(blob.text());
        })
      );
    };

    return this.http.get(url, { responseType: 'blob' }).pipe(
      switchMap((blob: Blob) => {
        if (this.isZipUrl(url)) {
          return unzipJsonBlob(blob);
        }
        return from(blob.slice(0, 4).arrayBuffer()).pipe(
          switchMap((buffer: ArrayBuffer) => {
            const bytes = new Uint8Array(buffer);
            const hasZipMagic = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4B; // 'PK'
            if (hasZipMagic) {
              return unzipJsonBlob(blob);
            }
            return from(blob.text());
          }),
          catchError(() => from(blob.text()))
        );
      }),
      map(parseJsonText)
    );
  }

  fetchFileFromUrl(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      switchMap(async (blob: Blob) => {
        // SIP uploads are zipped even when the storage URL ends in .dxf.
        // Inspect the bytes rather than the URL or Content-Type so compressed
        // data never reaches the DXF text tokenizer as a "PK" group code.
        const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
        const isZip = header[0] === 0x50 && header[1] === 0x4b && (
          (header[2] === 0x03 && header[3] === 0x04) ||
          (header[2] === 0x05 && header[3] === 0x06) ||
          (header[2] === 0x07 && header[3] === 0x08)
        );
        if (!isZip) return blob;

        let zip: JSZip;
        try {
          zip = await JSZip.loadAsync(await blob.arrayBuffer());
        } catch {
          throw new Error('Unable to open the downloaded ZIP archive. Please download the drawing again.');
        }
        const entry = Object.values(zip.files).find(file =>
          !file.dir && /\.(dxf|dwg)$/i.test(file.name),
        );
        if (!entry) throw new Error('Downloaded ZIP archive contains no DXF or DWG file.');
        // Propagate extraction errors; falling back to the archive would only
        // turn them into an unrelated DXF parse error in the editor.
        return entry.async('blob');
      }),
    );
  }

  pollExtractionStatus(submissionId: string, docType: string, stationCode: string, stationId: any, fileName: string): void {
    const cleanDocType = (docType || '').toLowerCase();
    if ((cleanDocType !== 'esp' && cleanDocType !== 'sip') || this.activePolls.has(submissionId)) return;

    const intervalId = setInterval(() => {
      const fetchStatus$ = stationId
        ? this.getStationDocumentVersions(stationId, docType).pipe(
            catchError(() => this.getSubmissionStatus(submissionId))
          )
        : this.getSubmissionStatus(submissionId);

      fetchStatus$.subscribe({
        next: (res: any) => {
          const versions = res?.versions || (Array.isArray(res) ? res : []);
          // Its own version, not the newest one sharing its file name — see findPolledVersion.
          const matchingVersion = findPolledVersion(versions, submissionId, fileName) ?? res;
          const status = (matchingVersion?.status_code || matchingVersion?.digitize_status || matchingVersion?.status || '').toUpperCase();

          if (['EXTRACTED', 'DIGITIZED', 'GENERATED', 'SYSTEM_GENERATED'].includes(status)) {
            this.stopPoll(submissionId);
            this.updateDocumentRowStatus(stationCode, docType, submissionId, status, res.approval_status || 'Approved', 'success');
            this.updateStation(stationId, { [docType]: 'generated' } as any).subscribe({
              next: () => this.getStations().subscribe({
                next: (stations: any) => {
                  const data = Array.isArray(stations) ? stations : (stations?.data || stations?.stations || []);
                  this.stationsSignal.set(data);
                  this.pollingCompleted.next({ submissionId, status: 'SUCCESS' });
                }
              })
            });
            this.messageService.add({
              severity: 'success',
              summary: 'Extraction Complete',
              detail: `Extraction complete for ${fileName}. Please review the results before proceeding.`
            });
          } else if (['FAILED', 'EXTRACTION_FAILED', 'EXTRACTION FAILED'].includes(status)) {
            this.stopPoll(submissionId);
            this.updateDocumentRowStatus(stationCode, docType, submissionId, 'FAILED', 'Rejected', 'danger');
            this.pollingCompleted.next({ submissionId, status: 'FAILED' });
            this.messageService.add({ severity: 'error', summary: 'Extraction Failed', detail: `Extraction failed for ${fileName}.` });
          }
        },
        error: (err: any) => {
          if (err.status === 404) this.stopPoll(submissionId);
        }
      });
    }, 5000);

    this.activePolls.set(submissionId, intervalId);
  }

  stopAllPolling(): void {
    this.activePolls.forEach(id => clearInterval(id));
    this.activePolls.clear();
  }

  private stopPoll(submissionId: string): void {
    clearInterval(this.activePolls.get(submissionId));
    this.activePolls.delete(submissionId);
  }

  private updateDocumentRowStatus(stationCode: string, docType: string, submissionId: string, status: string, approvalStatus: string, tone: string): void {
    const currentRows = this.stationDocuments()[stationCode.toUpperCase()] || { esp: [], sip: [], lop: [], survey: [] };
    const updatedList = (currentRows[docType] || []).map((doc: any) =>
      doc.submissionId === submissionId ? { ...doc, status, tone, approvalStatus } : doc
    );
    this.updateClonedRows(stationCode, { ...currentRows, [docType]: updatedList });
  }
}
